// store.ts
import type {
  AnyAction,
  Middleware,
  MiddlewareAPI,
  ThunkAction,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import { combineSlices, configureStore, isRejectedWithValue } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

// Slices / RTK Query
import { designSlice } from "../DesignTab/designSlice";
import { apiRTK } from "../apiRTK";
import { deviceSlice } from "../DesignTab/devices/deviceSlice";
import { roomSlice } from "../Room/RoomSlice";
import { McBimSlice } from "../McBim/McBimSlice";
import { debugSlice } from "../Debug/DebugSlice";
import { counterSlice } from "../counterSlice";
import undoable from "redux-undo";
import { makePatchSpyMiddleware } from "../patchSpyMiddleware";
import { globalSlice } from "../globalSlice";

// ag-Grid state slice + LS-middleware
import { agGridStateSlice } from "../agGridStateSlice";
import {
  agGridLocalStorageMiddleware,
  loadAgGridStateFromLS,
} from "../agGridStateLocalStorageMiddleware";
import { trainingSlice } from "../trainingdata/trainingSlice";

// --- Root reducer -----------------------------------------------------------
const rootReducer = combineSlices({
  global: globalSlice.reducer,
  design: designSlice.reducer,
  api: apiRTK.reducer,
  device: deviceSlice.reducer,
  room: roomSlice.reducer,
  mcbim: McBimSlice.reducer,
  debug: debugSlice.reducer,
  training: trainingSlice.reducer,
  counter: undoable(counterSlice.reducer),
  agGrid: agGridStateSlice.reducer, // ⬅︎ matchar selectAgGridStateByKey
});

export type IRootState = ReturnType<typeof rootReducer>;

const __DEV__ =
  typeof import.meta !== "undefined" ? import.meta.env.DEV : process.env.NODE_ENV !== "production";

// --- Optional: tyst logger för rejected actions -----------------------------
const rtkQueryErrorLogger: Middleware = (_api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    // console.warn("Rejected action:", action);
  }
  return next(action);
};

// --- Store factory ----------------------------------------------------------
export const makeStore = (preloadedState?: Partial<IRootState>) => {
  const baseMiddleware = (getDefaultMiddleware: any) =>
    getDefaultMiddleware().concat(
      rtkQueryErrorLogger,
      apiRTK.middleware,
      agGridLocalStorageMiddleware // ⬅︎ spegla agGrid.byKey → LocalStorage
    );

  // Hydrera från LS om inget preloadedState givits (eller om agGrid saknas)
  const hydrated = preloadedState ?? loadAgGridStateFromLS();

  const store = configureStore({
    reducer: rootReducer,
    middleware: (gdm) =>
      __DEV__ ? baseMiddleware(gdm).concat(makePatchSpyMiddleware(apiRTK)) : baseMiddleware(gdm),
    preloadedState: hydrated,
  });

  setupListeners(store.dispatch);
  return store;
};

// --- Singleton store + typer ------------------------------------------------
export const store = makeStore();
export type AppStore = typeof store;

// Viktigt: ge dispatch thunk-förmåga
export type AppDispatch = ThunkDispatch<IRootState, unknown, AnyAction>;

// Thunk-typ för dina egna thunks
export type IAppThunk<TReturn = void> = ThunkAction<TReturn, IRootState, unknown, AnyAction>;
